import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCRM } from '../context/CRMContext';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import LeadForm from '../components/Forms/LeadForm';
import LeadCard from '../components/Leads/LeadCard';
import SafeIcon from '../common/SafeIcon';
import supabase from '../lib/supabase';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import { NotificationService } from '../services/NotificationService';

const {
  FiPlus, FiFilter, FiSearch, FiUsers, FiTarget, FiTrendingUp,
  FiDollarSign, FiCalendar
} = FiIcons;

const Leads = () => {
    const { user: currentUser, isAdmin, isManager } = useAuth();
    const { users, addClient } = useCRM();
    const [leads, setLeads] = useState([]);
    const [leadSources, setLeadSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');

    useEffect(() => {
        loadLeads();
        loadLeadSources();
    }, []);

    const loadLeads = async () => {
        try {
            const { data, error } = await supabase
                .from('leads_crm_2025')
                .select(`*, lead_sources_crm_2025 (id, name)`)
                .order('created_at', { ascending: false });
            if (error) throw error;
            const formattedLeads = data?.map(lead => ({
                id: lead.id, leadName: lead.lead_name, leadEmail: lead.lead_email, leadPhone: lead.lead_phone,
                company: lead.company, leadSourceId: lead.lead_source_id, requiredServices: lead.required_services || [],
                status: lead.status, dateOfLead: lead.date_of_lead, contactedDate: lead.contacted_date,
                feedbackFromClient: lead.feedback_from_client, comments: lead.comments, reminderDateTime: lead.reminder_date_time,
                assignedTo: lead.assigned_to, createdBy: lead.created_by, estimatedValue: lead.estimated_value,
                priority: lead.priority, createdAt: lead.created_at, updatedAt: lead.updated_at,
                leadSource: lead.lead_sources_crm_2025
            })) || [];
            setLeads(formattedLeads);
        } catch (error) {
            toast.error('Failed to load leads');
        } finally {
            setLoading(false);
        }
    };

    const loadLeadSources = async () => {
        try {
            const { data, error } = await supabase.from('lead_sources_crm_2025').select('*').eq('is_active', true).order('name');
            if (error) throw error;
            const formattedSources = data?.map(source => ({ id: source.id, name: source.name, description: source.description, isActive: source.is_active })) || [];
            setLeadSources(formattedSources);
        } catch (error) {
            toast.error('Failed to load lead sources');
        }
    };

    const handleAddLead = () => {
        setIsModalOpen(true);
    };

    const handleSubmit = async (data) => {
        try {
            const leadData = {
                lead_name: data.leadName, lead_email: data.leadEmail, lead_phone: data.leadPhone || null,
                company: data.company || null, lead_source_id: data.leadSourceId, required_services: Array.isArray(data.requiredServices) ? data.requiredServices : [],
                status: data.status, date_of_lead: data.dateOfLead, contacted_date: data.contactedDate || null,
                feedback_from_client: data.feedbackFromClient || null, comments: data.comments || null,
                reminder_date_time: data.reminderDateTime || null, assigned_to: data.assignedTo || currentUser.id,
                created_by: currentUser.id, estimated_value: data.estimatedValue || null, priority: data.priority,
            };
            
            const { data: newLead, error } = await supabase.from('leads_crm_2025').insert([leadData]).select().single();
            if (error) throw error;
            
            toast.success('Lead added successfully');
            const assignedUser = users.find(u => u.id === newLead.assigned_to);
             if (assignedUser && assignedUser.id !== currentUser.id) {
                NotificationService.sendLeadAssignedEmail(newLead, assignedUser);
            }
            
            loadLeads();
            setIsModalOpen(false);
        } catch (error) {
            toast.error('Failed to add lead');
        }
    };

    const handleEditLead = async (leadId, data) => {
        try {
            const leadData = {
                lead_name: data.leadName, lead_email: data.leadEmail, lead_phone: data.leadPhone || null,
                company: data.company || null, lead_source_id: data.leadSourceId, required_services: Array.isArray(data.requiredServices) ? data.requiredServices : [],
                status: data.status, date_of_lead: data.dateOfLead, contacted_date: data.contactedDate || null,
                feedback_from_client: data.feedbackFromClient || null, comments: data.comments || null,
                reminder_date_time: data.reminderDateTime || null, assigned_to: data.assignedTo,
                estimated_value: data.estimatedValue || null, priority: data.priority, updated_at: new Date().toISOString(),
            };
            
            const { data: updatedLead, error } = await supabase.from('leads_crm_2025').update(leadData).eq('id', leadId).select().single();
            if (error) throw error;
            
            toast.success('Lead updated successfully');
            if (data.assignedTo !== data.originalAssignedTo) {
                 const assignedUser = users.find(u => u.id === updatedLead.assigned_to);
                 if (assignedUser) {
                    NotificationService.sendLeadAssignedEmail(updatedLead, assignedUser);
                }
            }
            loadLeads();
        } catch (error) {
            toast.error('Failed to update lead');
        }
    };

    const handleDeleteLead = async (leadId) => {
        if (window.confirm('Are you sure you want to delete this lead?')) {
            try {
                const { error } = await supabase.from('leads_crm_2025').delete().eq('id', leadId);
                if (error) throw error;
                toast.success('Lead deleted successfully');
                loadLeads();
            } catch (error) {
                toast.error('Failed to delete lead');
            }
        }
    };

    const handleConvertToClient = async (lead) => {
        try {
            const clientData = {
                name: lead.leadName, email: lead.leadEmail, phone: lead.leadPhone || '',
                company: lead.company || '', country: 'United States', address: '',
                notes: `Converted from lead. Original feedback: ${lead.feedbackFromClient || 'None'}`,
            };
            const newClient = await addClient(clientData);
            await supabase.from('leads_crm_2025').update({ status: 'converted', converted_to_client_id: newClient.id, updated_at: new Date().toISOString() }).eq('id', lead.id);
            toast.success('Lead converted to client successfully!');
            loadLeads();
        } catch (error) {
            toast.error('Failed to convert lead to client');
        }
    };

    const filteredLeads = leads.filter(lead => {
        if (!isAdmin && !isManager && lead.assignedTo !== currentUser.id) return false;
        if (searchTerm && !lead.leadName.toLowerCase().includes(searchTerm.toLowerCase()) && !lead.leadEmail.toLowerCase().includes(searchTerm.toLowerCase()) && !(lead.company && lead.company.toLowerCase().includes(searchTerm.toLowerCase()))) return false;
        if (statusFilter !== 'all' && lead.status !== statusFilter) return false;
        if (sourceFilter !== 'all' && lead.leadSourceId !== sourceFilter) return false;
        if (priorityFilter !== 'all' && lead.priority !== priorityFilter) return false;
        return true;
    });

    const stats = {
        total: filteredLeads.length,
        pending: filteredLeads.filter(l => l.status === 'pending').length,
        qualified: filteredLeads.filter(l => l.status === 'qualified').length,
        converted: filteredLeads.filter(l => l.status === 'converted').length,
        totalValue: filteredLeads.reduce((sum, lead) => sum + (lead.estimatedValue || 0), 0),
        myLeads: leads.filter(l => l.assignedTo === currentUser.id).length,
    };

    if (loading) {
        return <div className="flex items-center justify-center min-h-96"><div className="text-center"><div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto"></div><p className="mt-4 text-gray-600">Loading leads...</p></div></div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-gray-900">Lead Management</h1>
                <Button onClick={handleAddLead}><SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" /> Add Lead</Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
                <Card className="p-6"><div className="flex items-center"><div className="p-3 rounded-lg bg-blue-50"><SafeIcon icon={FiUsers} className="h-6 w-6 text-blue-600" /></div><div className="ml-4"><p className="text-sm font-medium text-gray-600">Total Leads</p><p className="text-2xl font-bold text-gray-900">{stats.total}</p></div></div></Card>
                <Card className="p-6"><div className="flex items-center"><div className="p-3 rounded-lg bg-yellow-50"><SafeIcon icon={FiCalendar} className="h-6 w-6 text-yellow-600" /></div><div className="ml-4"><p className="text-sm font-medium text-gray-600">Pending</p><p className="text-2xl font-bold text-gray-900">{stats.pending}</p></div></div></Card>
                <Card className="p-6"><div className="flex items-center"><div className="p-3 rounded-lg bg-purple-50"><SafeIcon icon={FiTarget} className="h-6 w-6 text-purple-600" /></div><div className="ml-4"><p className="text-sm font-medium text-gray-600">Qualified</p><p className="text-2xl font-bold text-gray-900">{stats.qualified}</p></div></div></Card>
                <Card className="p-6"><div className="flex items-center"><div className="p-3 rounded-lg bg-green-50"><SafeIcon icon={FiTrendingUp} className="h-6 w-6 text-green-600" /></div><div className="ml-4"><p className="text-sm font-medium text-gray-600">Converted</p><p className="text-2xl font-bold text-gray-900">{stats.converted}</p></div></div></Card>
                <Card className="p-6"><div className="flex items-center"><div className="p-3 rounded-lg bg-indigo-50"><SafeIcon icon={FiUsers} className="h-6 w-6 text-indigo-600" /></div><div className="ml-4"><p className="text-sm font-medium text-gray-600">My Leads</p><p className="text-2xl font-bold text-gray-900">{stats.myLeads}</p></div></div></Card>
                <Card className="p-6"><div className="flex items-center"><div className="p-3 rounded-lg bg-emerald-50"><SafeIcon icon={FiDollarSign} className="h-6 w-6 text-emerald-600" /></div><div className="ml-4"><p className="text-sm font-medium text-gray-600">Total Value</p><p className="text-2xl font-bold text-gray-900">${stats.totalValue.toLocaleString()}</p></div></div></Card>
            </div>
            
            <Card className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Search Leads</label><div className="relative"><SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" /><input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by name, email, or company" className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" /></div></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Status</label><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"><option value="all">All Status</option><option value="pending">Pending</option><option value="contacted">Contacted</option><option value="qualified">Qualified</option><option value="proposal_sent">Proposal Sent</option><option value="negotiation">Negotiation</option><option value="converted">Converted</option><option value="rejected">Rejected</option><option value="lost">Lost</option><option value="follow_up">Follow Up</option></select></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Source</label><select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"><option value="all">All Sources</option>{leadSources.map(source => (<option key={source.id} value={source.id}>{source.name}</option>))}</select></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Priority</label><select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"><option value="all">All Priorities</option><option value="urgent">Urgent</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
                    <div className="flex items-end"><Button variant="outline" onClick={() => { setSearchTerm(''); setStatusFilter('all'); setSourceFilter('all'); setPriorityFilter('all'); }} className="w-full"><SafeIcon icon={FiFilter} className="h-4 w-4 mr-2" /> Clear Filters</Button></div>
                </div>
            </Card>

            {filteredLeads.length === 0 ? (
                <Card className="p-12 text-center"><SafeIcon icon={FiUsers} className="h-12 w-12 text-gray-400 mx-auto mb-4" /><h3 className="text-lg font-medium text-gray-900 mb-2">{leads.length === 0 ? 'No leads yet' : 'No leads match your filters'}</h3><p className="text-gray-500 mb-4">{leads.length === 0 ? 'Start by adding your first lead to track potential customers.' : 'Try adjusting your search criteria or filters.'}</p>{leads.length === 0 && (<Button onClick={handleAddLead}><SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" /> Add Lead</Button>)}</Card>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {filteredLeads.map((lead, index) => (
                        <motion.div key={lead.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                            <LeadCard lead={lead} onEdit={handleEditLead} onDelete={handleDeleteLead} onConvert={handleConvertToClient} />
                        </motion.div>
                    ))}
                </div>
            )}

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Add New Lead" size="xl">
                <LeadForm onSubmit={handleSubmit} onCancel={() => setIsModalOpen(false)} />
            </Modal>
        </div>
    );
};

export default Leads;